import { useMemo, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowLeft, ArrowUp, Calendar, Car, ChevronRight, ChevronsUpDown, Layers } from 'lucide-react'
import { publicAPI, type AndreKjoretoyParams } from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import ErrorMessage from '../../components/ErrorMessage'
import SearchBar from '../../components/SearchBar'
import PostnummerFilter from '../../components/PostnummerFilter'
import ExportButtons from '../../components/ExportButtons'
import ChangeIndicator, { ChangeCount } from '../../components/ChangeIndicator'
import QueryExplanationBox, { buildExplanationText } from '../../components/QueryExplanationBox'
import { ColumnDefinition } from '../../utils/exportToCsv'
import { formatNorwegianDate } from '../../utils/dateUtils'
import { usePersistedState } from '../../hooks/usePersistedState'
import { useMinePostnummer } from '../../hooks/useMinePostnummer'
import { useMaxDataDate } from '../../hooks/useMaxDataDate'
import { useYear } from '../../contexts/YearContext'
import { localizeApiDetail } from '../../i18n/apiError'
import { useLatest } from '../Bruktbil/useLatest'
import { DEFAULT_GRUPPE, isGruppe, type Gruppe } from './grupper'

interface PeriodInfo { label: string; start_date: string; end_date: string }
interface PeriodsInfo {
  current: PeriodInfo
  previous: PeriodInfo
  ytd_current: PeriodInfo | null
  ytd_previous: PeriodInfo | null
}

interface Row {
  ranking: number | null
  brand: string
  model: string | null
  current_count: number
  current_market_share: number
  previous_count: number
  previous_market_share: number
  change_count: number
  change_percent: number | null
  ytd_current_count: number | null
  ytd_previous_count: number | null
  ytd_change_count: number | null
  ytd_change_percent: number | null
}

type OutputMode = 'year_to_date' | 'month_to_date' | 'last_month' | 'last_week' | 'last_year' | 'yesterday' | 'custom'
type Type = AndreKjoretoyParams['type']
type SortConfig = { key: keyof Row; direction: 'asc' | 'desc' } | null

// Same chips, in the same order, as Nybilstatistikk.
const OUTPUT_MODES: OutputMode[] = [
  'year_to_date', 'month_to_date', 'last_month', 'last_week', 'yesterday', 'last_year', 'custom',
]
// A past year has no "yesterday" or "last week" (#982 decision 4).
const CURRENT_YEAR_ONLY: OutputMode[] = ['yesterday', 'last_week']
const TYPES: Type[] = ['ny', 'bruktimport', 'alle']

const nb = (n: number | null | undefined) => (n ?? 0).toLocaleString('nb-NO')
const pct = (n: number | null | undefined) =>
  (n ?? 0).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

export default function AndreKjoretoy() {
  const { gruppe } = useParams()
  if (!isGruppe(gruppe)) return <Navigate to={`/andre-kjoretoy/${DEFAULT_GRUPPE}`} replace />
  // A new class starts on its Merke level (#982), so the page remounts per class.
  return <AndreKjoretoyKlasse key={gruppe} gruppe={gruppe} />
}

function AndreKjoretoyKlasse({ gruppe }: { gruppe: Gruppe }) {
  const { t } = useTranslation(['andreKjoretoy', 'analytics'])
  const klasse = t(`klasser.${gruppe}`)

  const [tab, setTab] = useState<'merke' | 'modell'>('merke')
  const [drillMerke, setDrillMerke] = useState<string | null>(null)
  const [outputMode, setOutputMode] = usePersistedState<OutputMode>('bp:filter:outputMode', 'year_to_date')
  const [type, setType] = usePersistedState<Type>('andre-kjoretoy:type', 'alle')
  const [postnummerEnabled, setPostnummerEnabled] = usePersistedState('andre-kjoretoy:postnummerFilterEnabled', false)
  const minePostnummer = useMinePostnummer(postnummerEnabled)
  const [customStart, setCustomStart] = usePersistedState<string>('bp:filter:customStartDate', '')
  const [customEnd, setCustomEnd] = usePersistedState<string>('bp:filter:customEndDate', '')
  const [applied, setApplied] = useState({ start: customStart, end: customEnd, tick: 0 })
  const maxDataDate = useMaxDataDate()
  const { selectedYear } = useYear()

  const latestYear = maxDataDate ? Number(maxDataDate.slice(0, 4)) : new Date().getFullYear()
  const aar = Math.min(Number(selectedYear), latestYear)
  const isPastYear = aar < latestYear
  const mode: OutputMode = isPastYear && CURRENT_YEAR_ONLY.includes(outputMode) ? 'year_to_date' : outputMode
  const customBlocked = mode === 'custom' && (!applied.start || !applied.end)
  const hasYtd = mode !== 'year_to_date' && mode !== 'custom' && mode !== 'last_year'
  const level = tab === 'modell' ? 'modeller' : drillMerke ? 'merkeModeller' : 'merker'

  const [loading, setLoading] = useState(!customBlocked)
  const [error, setError] = useState<string | null>(null)
  const [periods, setPeriods] = useState<PeriodsInfo | null>(null)
  const [summary, setSummary] = useState<Row | null>(null)
  const [rows, setRows] = useState<Row[]>([])
  const [queried, setQueried] = useState({ mode: mode as string, postnummer: minePostnummer, type })
  const [search, setSearch] = useState('')
  const [sortConfig, setSortConfig] = useState<SortConfig>(null)

  const customKey = mode === 'custom' ? `${applied.start}|${applied.end}` : ''
  const fetchKey = [gruppe, level, drillMerke, mode, type, aar, minePostnummer, customKey, applied.tick].join('|')

  useLatest(alive => {
    if (customBlocked) return
    const params: AndreKjoretoyParams = { gruppe, output_mode: mode, type, limit: 2000 }
    if (mode === 'custom') {
      params.custom_start_date = applied.start
      params.custom_end_date = applied.end
    } else {
      params.aar = aar
    }
    if (minePostnummer) params.mine_postnummer = minePostnummer
    setLoading(true)
    setError(null)
    const request = level === 'merker'
      ? publicAPI.getAndreKjoretoyMerker(params)
      : publicAPI.getAndreKjoretoyModeller(drillMerke ? { ...params, merke: drillMerke } : params)
    request
      .then(response => {
        if (!alive()) return
        const [total, ...data] = response.data.data as Row[]
        setPeriods(response.data.periods)
        setSummary(total ?? null)
        setRows(data)
        setQueried({ mode, postnummer: minePostnummer, type })
      })
      .catch((err: { response?: { data?: { detail?: string } }; message?: string }) => {
        if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('analytics:common.loadFailed'))
      })
      .finally(() => { if (alive()) setLoading(false) })
  }, [fetchKey])

  const nameField: 'brand' | 'model' = level === 'merker' ? 'brand' : 'model'
  const nameLabel = level === 'merker' ? t('analytics:common.brand') : t('analytics:common.model')

  const shown = useMemo(() => {
    const needle = search.toLowerCase()
    const filtered = needle ? rows.filter(r => (r[nameField] ?? '').toLowerCase().includes(needle)) : rows
    if (!sortConfig) return filtered
    const { key, direction } = sortConfig
    const sign = direction === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      const av = a[key], bv = b[key]
      if (typeof av === 'string' || typeof bv === 'string') return sign * String(av ?? '').localeCompare(String(bv ?? ''))
      return sign * ((av ?? -Infinity) as number) - sign * ((bv ?? -Infinity) as number)
    })
  }, [rows, search, sortConfig, nameField])

  const handleSort = (key: keyof Row) => {
    if (sortConfig?.key !== key) setSortConfig({ key, direction: 'asc' })
    else if (sortConfig.direction === 'asc') setSortConfig({ key, direction: 'desc' })
    else setSortConfig(null)
  }
  const sortIcon = (key: keyof Row) => sortConfig?.key === key
    ? (sortConfig.direction === 'asc' ? <ArrowUp className="w-4 h-4 inline ml-1" /> : <ArrowDown className="w-4 h-4 inline ml-1" />)
    : <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />

  const openMerke = (brand: string) => {
    setDrillMerke(brand)
    setSearch('')
    setSortConfig(null)
  }
  const backToMerker = () => {
    setDrillMerke(null)
    setSearch('')
    setSortConfig(null)
  }
  const switchTab = (next: 'merke' | 'modell') => {
    setTab(next)
    setDrillMerke(null)
    setSearch('')
    setSortConfig(null)
  }

  const typeLine = t(`type.filter${queried.type === 'ny' ? 'Ny' : queried.type === 'bruktimport' ? 'Bruktimport' : 'Alle'}`)
  const drillDownLabel = drillMerke ? t('modelsFor', { brand: drillMerke }) : null
  const explanation = {
    periods,
    maxDataDate: null,
    outputMode: queried.mode,
    vehicleFilter: null,
    postnummerFilter: queried.postnummer,
    drillDownLabel,
    filterLines: [`${klasse}.`, typeLine],
  }

  const exportTitle = drillMerke
    ? t('export.modelsFor', { klasse, brand: drillMerke })
    : level === 'modeller' ? t('export.models', { klasse }) : t('export.brands', { klasse })
  const exportColumns: ColumnDefinition[] = [
    { key: 'ranking', label: 'Ranking' },
    ...(level === 'modeller' ? [{ key: 'brand', label: t('analytics:common.brand') }] : []),
    { key: nameField, label: nameLabel },
    { key: 'current_count', label: periods?.current.label || t('analytics:total.now') },
    { key: 'current_market_share', label: t('analytics:total.export.shareOf', { label: periods?.current.label || t('analytics:total.export.nowLower') }), format: v => Number(v ?? 0).toFixed(1) },
    { key: 'previous_count', label: periods?.previous.label || t('analytics:total.previous') },
    { key: 'previous_market_share', label: t('analytics:total.export.shareOf', { label: periods?.previous.label || t('analytics:total.export.previousLower') }), format: v => Number(v ?? 0).toFixed(1) },
    { key: 'change_count', label: t('analytics:common.change') },
    { key: 'change_percent', label: t('analytics:common.changePct'), format: v => v !== null ? Number(v).toFixed(1) : '-' },
    ...(hasYtd ? [
      { key: 'ytd_current_count', label: periods?.ytd_current?.label || t('analytics:total.ytdNow') },
      { key: 'ytd_previous_count', label: periods?.ytd_previous?.label || t('analytics:total.ytdPrevious') },
      { key: 'ytd_change_count', label: t('analytics:total.export.ytdChange') },
      { key: 'ytd_change_percent', label: t('analytics:total.export.ytdChangePct'), format: (v: unknown) => v !== null ? Number(v).toFixed(1) : '-' },
    ] : []),
  ]

  const th = 'text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
  const sep = 'border-r-2 border-gray-300 dark:border-gray-600'

  return (
    <div className="space-y-6">
      {drillMerke ? (
        <div className="flex items-center gap-4">
          <button onClick={backToMerker}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-dark-card hover:bg-gray-200 dark:hover:bg-dark-border rounded-lg transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span>{t('analytics:common.back')}</span>
          </button>
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <span className="hover:text-primary-600 cursor-pointer" onClick={backToMerker}>{t('title')}</span>
              <ChevronRight className="w-3 h-3" />
              <span className="hover:text-primary-600 cursor-pointer" onClick={backToMerker}>{klasse}</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-gray-900 dark:text-gray-100 font-medium">{drillMerke}</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('modelsHeading', { brand: drillMerke })}</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">{t('modelsSubtitle')}</p>
          </div>
        </div>
      ) : (
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('titleWithClass', { klasse })}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">{t('subtitle')}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('source')}</p>
        </div>
      )}

      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('analytics:total.period')}</span>
          <div className="flex flex-wrap gap-2">
            {OUTPUT_MODES.map(m => {
              const disabled = isPastYear && CURRENT_YEAR_ONLY.includes(m)
              return (
                <button key={m} type="button" disabled={disabled}
                  title={disabled ? t('pastYearDisabled') : undefined}
                  onClick={() => setOutputMode(m)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    disabled
                      ? 'bg-gray-100 dark:bg-dark-card text-gray-300 dark:text-gray-600 cursor-not-allowed'
                      : mode === m
                        ? 'bg-primary-600 text-white shadow-xs'
                        : 'bg-gray-100 dark:bg-dark-card text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-border'
                  }`}>
                  {t(`analytics:outputModes.${m}`)}
                </button>
              )
            })}
          </div>
          {mode === 'custom' && (
            <div className="flex items-end gap-3">
              <div>
                <label htmlFor="ak-start" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('analytics:total.startDate')}</label>
                <input id="ak-start" type="date" value={customStart} max={maxDataDate || undefined}
                  onChange={e => setCustomStart(e.target.value)} className="input-field" />
              </div>
              <div>
                <label htmlFor="ak-end" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('analytics:total.endDate')}</label>
                <input id="ak-end" type="date" value={customEnd} max={maxDataDate || undefined}
                  onChange={e => setCustomEnd(e.target.value)} className="input-field" />
              </div>
            </div>
          )}
          <div className="ml-auto flex items-center gap-3">
            <div role="group" aria-label={t('type.label')}
              className="flex rounded-lg border border-gray-200 dark:border-dark-border overflow-hidden">
              {TYPES.map((value, i) => (
                <button key={value} type="button" aria-pressed={type === value} onClick={() => setType(value)}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    type === value
                      ? 'bg-primary-500 text-white'
                      : 'bg-white dark:bg-dark-card text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-border'
                  } ${i > 0 ? 'border-l border-gray-200 dark:border-dark-border' : ''}`}>
                  {t(`type.${value}`)}
                </button>
              ))}
            </div>
            <button type="button"
              disabled={mode === 'custom' && (!customStart || !customEnd)}
              onClick={() => setApplied(a => ({ start: customStart, end: customEnd, tick: a.tick + 1 }))}
              className={`btn-primary text-sm ${mode === 'custom' && (!customStart || !customEnd) ? 'opacity-50 cursor-not-allowed' : ''}`}>
              {t('analytics:common.update')}
            </button>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-border">
          <PostnummerFilter enabled={postnummerEnabled} onToggle={setPostnummerEnabled} compact />
        </div>
      </div>

      {!drillMerke && (
        <div role="tablist" className="flex gap-2 border-b border-gray-200 dark:border-dark-border">
          {(['merke', 'modell'] as const).map(value => (
            <button key={value} type="button" role="tab" aria-selected={tab === value} onClick={() => switchTab(value)}
              className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                tab === value
                  ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}>
              <span className="flex items-center gap-2">
                {value === 'merke' ? <Car className="w-4 h-4" /> : <Layers className="w-4 h-4" />}
                {t(`tabs.${value}`)}
              </span>
            </button>
          ))}
        </div>
      )}

      <QueryExplanationBox {...explanation} />

      {loading && <LoadingSpinner />}
      {error && !loading && <ErrorMessage message={error} />}

      {!loading && !error && !customBlocked && (
        <div className="card p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              {sortConfig && (
                <button onClick={() => setSortConfig(null)} className="btn-secondary text-sm">{t('analytics:common.resetSort')}</button>
              )}
            </div>
            <ExportButtons
              data={summary ? [{ ...summary, ranking: '∑', [nameField]: 'TOTAL' }, ...shown] : shown}
              columns={exportColumns}
              metadata={{
                title: exportTitle,
                dateRange: periods ? t('analytics:common.dateRange', {
                  start: formatNorwegianDate(periods.current.start_date), end: formatNorwegianDate(periods.current.end_date),
                }) : '',
                filters: [klasse, typeLine, queried.postnummer ? t('analytics:common.postnummerFilter', { value: queried.postnummer }) : '']
                  .filter(Boolean),
                explanationText: buildExplanationText(explanation),
              }}
            />
          </div>

          <div className="mb-4">
            <SearchBar value={search} onChange={setSearch}
              placeholder={level === 'merker' ? t('searchBrand') : t('searchModel')} />
          </div>

          {rows.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <p className="text-gray-500 dark:text-gray-400">{t('noData')}</p>
              {postnummerEnabled && (
                <button type="button" onClick={() => setPostnummerEnabled(false)} className="btn-secondary text-sm">
                  {t('turnOffPostnummer')}
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table data-table-compact">
                <thead>
                  <tr>
                    <th className="text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('ranking')}># {sortIcon('ranking')}</th>
                    {level === 'modeller' && (
                      <th className="text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('brand')}>{t('analytics:common.brand')} {sortIcon('brand')}</th>
                    )}
                    <th className="text-left max-w-[220px] cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort(nameField)}>{nameLabel} {sortIcon(nameField)}</th>
                    <th className={th} onClick={() => handleSort('current_count')}>{periods?.current.label || t('analytics:total.now')} {sortIcon('current_count')}</th>
                    <th className={`${th} ${sep}`} onClick={() => handleSort('current_market_share')}>{t('analytics:total.share')} {sortIcon('current_market_share')}</th>
                    <th className={th} onClick={() => handleSort('previous_count')}>{periods?.previous.label || t('analytics:total.previous')} {sortIcon('previous_count')}</th>
                    <th className={`${th} ${sep}`} onClick={() => handleSort('previous_market_share')}>{t('analytics:total.share')} {sortIcon('previous_market_share')}</th>
                    <th className={th} onClick={() => handleSort('change_count')}>+/- {sortIcon('change_count')}</th>
                    <th className={th} onClick={() => handleSort('change_percent')}>{t('analytics:common.changePct')} {sortIcon('change_percent')}</th>
                    {hasYtd && (
                      <>
                        <th className={`${th} border-l-2 border-gray-300 dark:border-gray-600`} onClick={() => handleSort('ytd_current_count')}>{periods?.ytd_current?.label || t('analytics:total.ytdNow')} {sortIcon('ytd_current_count')}</th>
                        <th className="text-right">{periods?.ytd_previous?.label || t('analytics:total.ytdPrevious')}</th>
                        <th className={th} onClick={() => handleSort('ytd_change_percent')}>{t('analytics:total.ytdChangePctHeader')} {sortIcon('ytd_change_percent')}</th>
                      </>
                    )}
                    {level === 'merker' && <th className="text-center">{t('analytics:common.action')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {summary && (
                    <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
                      <td className="text-center py-2"><span className="text-primary-600 dark:text-primary-400 font-bold">∑</span></td>
                      {level === 'modeller' && <td className="py-2"></td>}
                      <td className="font-bold text-primary-700 dark:text-primary-300 py-2">TOTAL</td>
                      <td className="text-right py-2">{nb(summary.current_count)}</td>
                      <td className={`text-right py-2 ${sep}`}><span className="text-primary-600 dark:text-primary-400">100,0</span></td>
                      <td className="text-right py-2">{nb(summary.previous_count)}</td>
                      <td className={`text-right py-2 ${sep}`}><span className="text-primary-600 dark:text-primary-400">100,0</span></td>
                      <td className="text-right py-2"><ChangeCount value={summary.change_count} /></td>
                      <td className="text-right py-2"><ChangeIndicator value={summary.change_percent} showIcon={false} /></td>
                      {hasYtd && (
                        <>
                          <td className="text-right py-2 border-l-2 border-gray-300 dark:border-gray-600">{nb(summary.ytd_current_count)}</td>
                          <td className="text-right py-2">{nb(summary.ytd_previous_count)}</td>
                          <td className="text-right py-2"><ChangeIndicator value={summary.ytd_change_percent} showIcon={false} /></td>
                        </>
                      )}
                      {level === 'merker' && <td className="text-center py-2 text-gray-500 dark:text-gray-400">—</td>}
                    </tr>
                  )}
                  {shown.map((row, idx) => (
                    <tr key={`${row.brand}|${row.model ?? ''}`}
                      className={`hover:bg-gray-100 dark:hover:bg-dark-border transition-colors ${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''} ${level === 'merker' ? 'cursor-pointer' : ''}`}
                      onClick={level === 'merker' ? () => openMerke(row.brand) : undefined}>
                      <td className="text-center text-gray-700 dark:text-gray-300">{row.ranking}</td>
                      {level === 'modeller' && <td className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{row.brand}</td>}
                      <td className="max-w-[220px] truncate font-bold">{row[nameField]}</td>
                      <td className="text-right">{nb(row.current_count)}</td>
                      <td className={`text-right ${sep}`}>{pct(row.current_market_share)}</td>
                      <td className="text-right">{nb(row.previous_count)}</td>
                      <td className={`text-right ${sep}`}><span className="text-gray-600 dark:text-gray-400">{pct(row.previous_market_share)}</span></td>
                      <td className="text-right"><ChangeCount value={row.change_count} /></td>
                      <td className="text-right"><ChangeIndicator value={row.change_percent} showIcon={false} /></td>
                      {hasYtd && (
                        <>
                          <td className="text-right border-l-2 border-gray-200 dark:border-gray-700">{row.ytd_current_count !== null ? nb(row.ytd_current_count) : '-'}</td>
                          <td className="text-right">{row.ytd_previous_count !== null ? nb(row.ytd_previous_count) : '-'}</td>
                          <td className="text-right"><ChangeIndicator value={row.ytd_change_percent} showIcon={false} /></td>
                        </>
                      )}
                      {level === 'merker' && (
                        <td className="text-center">
                          <button type="button" onClick={e => { e.stopPropagation(); openMerke(row.brand) }}
                            className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline">
                            <span className="text-xs font-medium">{t('actionModels')}</span>
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
